import { MetricFetchOutcome } from 'api/customerInsights';

import {
  CardMetricScope,
  identifiersNamedBy,
  MetricIdentity,
  metricIdentitiesForCard,
  MetricSource,
  RatioOperand
} from '../registry/metricIdentifiers';
import { productIdOfMetricKey } from '../registry/productReadiness';
import {
  CardDataState,
  CardDistribution,
  CardMetric,
  CardMetricEvidence,
  IntelligenceFixture,
  MetricFactor
} from '../types';
import { CardConfidenceInput } from './cardConfidence';
import { cardStateFromOutcome } from './cardStateFromOutcome';
import { customer360CardConfidence } from './customer360CardConfidence';
import { customerInsightsCardConfidence } from './customerInsightsCardConfidence';
// DEMO ONLY, PLA-8828 2026-09-21 — delete this import and the one call below with `demoFallback.ts`.
import { withDemoFallback } from './demoFallback';
import { withDemoNarrative } from './demoFallbackWhy';
import { detailEvidencePanel, reversalEvidencePanel } from './evidenceRows';
import {
  asPercentage,
  calculationBasisOf,
  confidenceOf,
  MetricDistribution,
  MetricResult,
  toCardMetric,
  toMetricResult
} from './metricResult';
import { readinessCardMetric } from './productReadinessMetric';
import { reversalCardMetric } from './reversalMetric';
import { WtrReason } from './wtr';

// Per-card assembly: N metric readings into the one payload a card renders. `metricResult.ts` owns
// the wire parse, the union and the delta gates; this decides which tiles exist, in what order, and
// what the card as a whole says. PLA-8815, plan § V.7.
//
// The only arithmetic that lives here is the `ratio` and `difference` kinds' — one subtraction and
// the shared `asPercentage`, which moved to `metricResult.ts` once Product Readiness needed the same
// rounding for a rate it derives from its own rows (PLA-8815).

/**
 * A tile the six-column contract cannot fill, and why.
 *
 * `derived` — the tile is a COMPONENT of a real metric (its numerator, its denominator, the cohort
 * it is taken over, the top row of its distribution). The metric is fetched; the component is not a
 * column. `unmapped` — no catalogue metric corresponds at all.
 *
 * 42 of the page's 96 tiles are one of these. They are returned NAMED rather than dropped, because a
 * card that quietly renders three tiles where the design draws four looks complete and is not — and
 * a blank tile beside three populated ones reads as a zero that failed to load. What these tiles
 * eventually SHOW is a product decision waiting on the backend (draft § 1); that they cannot be
 * silently absent is not.
 */
export interface UnservableTile {
  metricKey: string;
  reason: 'derived' | 'unmapped';
  /** The catalogue metric this tile is a component of. Absent when nothing corresponds. */
  from?: string;
  /** The tile's own display label — what a reader sees, never the catalogue id or the internal key. */
  label: string;
}

export interface CardAssembly {
  fixture: IntelligenceFixture;
  unservable: UnservableTile[];
  /** Catalogue identifiers this card needs fetched, `derived` sources included. */
  identifiers: string[];
}

/**
 * A tile that IS a segment set rather than a figure — a `distribution` source with no `component`.
 *
 * Split out because these two things need opposite handling and share a kind: a distribution tile
 * produces a `CardDistribution` and NO `CardMetric` (there is no single value to put on one, and
 * `InsightCardRenderer` renders every `CardMetric` as a `StatBox`), while a component tile on the
 * same response produces an ordinary `CardMetric` and no segments.
 */
const isSegmentTile = (source: MetricSource): boolean =>
  source.kind === 'distribution' && source.component === undefined;

// Worst-first: a card whose metrics failed for different reasons reports the most serious, because
// the reader acting on "temporarily broken", on "not set up here" and on "not landed yet" does
// three different things.
const STATE_PRECEDENCE: CardDataState['kind'][] = [
  'technicalError',
  'notConfigured',
  'awaitingData'
];

export const worstOf = (states: CardDataState[]): CardDataState | undefined =>
  STATE_PRECEDENCE.map(kind => states.find(state => state.kind === kind)).find(Boolean);

// The freshest period any calculated reading covers. Absent rather than "now": the payload states
// when the DATA is from, and a render timestamp would be a different fact wearing the same label.
export const latestPeriodEnd = (outcomes: MetricFetchOutcome[]): string | null => {
  const ends = outcomes
    .filter(outcome => outcome.kind === 'rows')
    .flatMap(outcome => (outcome as { rows: { period_end: string }[] }).rows)
    .map(row => row.period_end)
    .filter(end => end && Number.isFinite(Date.parse(end)));

  return ends.length ? ends.sort().slice(-1)[0] : null;
};

/**
 * The catalogue identifiers one card needs fetched, `derived` sources included.
 *
 * Separate from `assembleCard` because the fetch has to happen BEFORE the assembly it feeds, and
 * calling the assembler with no outcomes just to read this back would make the fetch look like it
 * depended on its own result.
 *
 * `scope` carries the render-time parameters that decide which metric a card reads — the Product
 * Readiness dropdown's product, and nothing else today. Every other card ignores it.
 */
export const identifiersForCard = (
  cardKey: string,
  scope?: CardMetricScope,
  // Which registry the card's tiles come from, as opposed to `scope`, which picks WHICH metric
  // inside one. Defaults to Customer Insights', so every existing call site is unchanged; Customer
  // 360 passes its own because it is a different card set behind different permissions
  // (`registry/customer360MetricIdentifiers.ts`). PLA-8828.
  entries: MetricIdentity[] = metricIdentitiesForCard(cardKey, scope)
): string[] =>
  Array.from(
    new Set(
      // The fan-out is the registry's own (`identifiersNamedBy`), not a second copy of it: an
      // arithmetic entry contributes BOTH operands, and a kind provisioned here but not in
      // `liveIdentifiers` would fetch on the page and be missing from the backend ask.
      entries.flatMap(entry => identifiersNamedBy(entry.source))
    )
  );

/**
 * A score's contributing factors, off its own row, strongest first.
 *
 * `tone` is the factor's declared `effect` resolved against the factor set's own `riseIs` -- raising
 * a factor that raises a score a rise in is BAD is a blocker, and so is lowering one that helps.
 * Neither half is read off the column's name, and neither is `riseMeans`, which carries the delta
 * chip's drawn hue and disagrees with the formula on two of these metrics. Ranked by MAGNITUDE and not by the formula's order, because the rail states which factors
 * are moving this period.
 *
 * `undefined` -- never `[]` -- when the metric declares no factors or the response carried none of
 * them, so "states no factors" stays apart from "stated some, all withheld". A factor whose column
 * is missing is dropped rather than ranked at zero, for the reason every other reading here is.
 * PLA-8815.
 */
const factorsOf = (entry: MetricIdentity, results: MetricResult[]): MetricFactor[] | undefined => {
  const current = results[0];

  if (!entry.factors || !current || current.status !== 'calculated') {
    return undefined;
  }

  const { riseIs, terms } = entry.factors;

  const rows = terms.reduce<MetricFactor[]>((kept, factor) => {
    const value = current.components[factor.column];

    return typeof value === 'number'
      ? [
          ...kept,
          {
            key: factor.column,
            label: factor.label,
            value,
            tone:
              (factor.effect === 'raises') === (riseIs === 'good')
                ? ('positive' as const)
                : ('blocker' as const)
          }
        ]
      : kept;
  }, []);

  return rows.length > 0
    ? [...rows].sort((left, right) => Math.abs(right.value) - Math.abs(left.value))
    : undefined;
};

/**
 * Which layer's `Show Confidence Score?` table the interim card score is computed against.
 *
 * A LAYER and not a boolean, and that is the whole cross-contamination guard. `product_readiness`,
 * `ready_for_engagement`, `contact_restrictions` and `risk_unusual_activity` are card keys BOTH
 * registries declare, so "should this card draw a pill" cannot be answered from the card key: it is
 * answered by the caller naming its own catalogue, and each catalogue's table returns `undefined`
 * for the other's identifiers, which is never `'yes'` and so never eligible. Customer 360 switching
 * the opt-in on therefore cannot draw a pill on Layer 1's same-named card, in either direction.
 *
 * Both layers compute through the SAME rule (`data/cardConfidence.ts`); only the table differs.
 * PLA-8815, PLA-8828.
 */
export type InterimCardConfidenceLayer = 'customer_insights' | 'customer_360';

const INTERIM_CARD_CONFIDENCE: Record<
  InterimCardConfidenceLayer,
  (inputs: CardConfidenceInput[]) => IntelligenceFixture['confidence']
> = {
  customer_insights: customerInsightsCardConfidence,
  customer_360: customer360CardConfidence
};

/**
 * One card's payload from its metrics' fetch outcomes.
 *
 * `outcomes` is keyed by catalogue identifier, not by metric key, because that is the fetch unit:
 * one call per metric, and two tiles of the same card routinely read the same metric.
 */
export const assembleCard = (
  cardKey: string,
  outcomes: Record<string, MetricFetchOutcome>,
  scope?: CardMetricScope,
  // PLA-8815 / PLA-8828 INTERIM, opt-in per caller: see `data/cardConfidence.ts`, the computation
  // the backend owns and has not shipped. Absent by default, so a card publishes no score unless
  // its caller names the catalogue it is reading.
  interimCardConfidence?: InterimCardConfidenceLayer,
  // The registry rows this card is built from. Defaulted from the Layer 1 registry so every existing
  // call site is unchanged; Customer 360 passes its own, which is what makes the four shared card
  // keys resolve to `C-*` identifiers here rather than `O-*`.
  entries: MetricIdentity[] = metricIdentitiesForCard(cardKey, scope),
  // The rendering tenant, for the demo layer alone — nothing on the real assembly path reads it.
  // Last and optional because the two live sources are its only informed callers; an omitted name
  // resolves to NOT allowed in `demoFallbackAllowed`, so forgetting it costs the demo its figures
  // and can never hand a tenant invented ones.
  tenantName?: string | null
): CardAssembly => {
  const unservable: UnservableTile[] = [];
  const identifiers: string[] = [];
  const servedOutcomes: MetricFetchOutcome[] = [];
  // Keyed by identifier rather than collected flat, so the metric pass can put the state that failed
  // onto the tile that asked for it as well as into the card's own verdict. One entry per identifier
  // even when two tiles read it: `cardStateFromOutcome` is a pure function of the outcome, so a
  // second reading could only ever repeat the first. PLA-8815 + PLA-8828.
  const failedStates: Record<string, CardDataState> = {};
  // Populated when a `metric`-kind tile was fetched and answered but `calculation_status` was not
  // `calculated` — the backend's own reason, kept apart from `unservable` because those tiles were
  // never fetchable to begin with; these were fetched and answered "not this time, and here's why".
  const insufficientReasons: string[] = [];
  /**
   * The breakdown rows this card's metrics answered with alongside their readings, in registry
   * order, each tagged with the metric it came from.
   *
   * ALL of them are carried: `engagement_timeline` has two multi-row metrics (`C-COM-010` funnel
   * stages, `C-COM-009` engagement sources) and keeping only the first left the second's rows on the
   * wire with a panel drawn empty over them. `evidence` below still publishes the first, unchanged.
   * PLA-8828.
   */
  const evidencePanels: CardMetricEvidence[] = [];

  // Parsed ONCE per catalogue identifier, before any tile is built. Two tiles of the same card
  // routinely read the same metric — a `ratio` tile and the `metric` tile its numerator comes from
  // always do — and parsing the same outcome twice would hand them two different object graphs for
  // the same reading.
  const resultsByIdentifier: Record<string, MetricResult[]> = {};
  // The non-scalar half of the same parse, keyed the same way and for the same reason: the two
  // segment tiles on Customer Lifecycle & Product Holdings read O-COM-032 alongside two component
  // tiles, so its response is parsed once here rather than once per tile.
  const distributionsByIdentifier: Record<string, MetricDistribution> = {};
  // Parsed the same way and for the same reason: the reasons are a property of the METRIC, so two
  // tiles reading one identifier share one ranked list rather than two. PLA-8828.
  const reasonsByIdentifier: Record<string, WtrReason[]> = {};

  // `entries`, not the default lookup: omitting it resolves the identifiers through Customer
  // Insights' registry, which knows nothing of the Customer 360 card keys — so this index came back
  // empty, `toCardMetric` was handed `[]` for every tile, and a card whose metrics had answered with
  // real readings rendered `Not Configured` with nothing named as missing. PLA-8828.
  identifiersForCard(cardKey, scope, entries).forEach(identifier => {
    const outcome = outcomes[identifier];

    if (outcome && outcome.kind === 'rows') {
      resultsByIdentifier[identifier] = outcome.rows.map(toMetricResult);

      if (outcome.distribution) {
        distributionsByIdentifier[identifier] = outcome.distribution;
      }

      if (outcome.reasons && outcome.reasons.length > 0) {
        reasonsByIdentifier[identifier] = outcome.reasons;
      }
    }
  });

  /**
   * One side of a `ratio` or a `difference`, or nothing.
   *
   * `undefined` — never a zero and never a fallback — for every way an operand can fail to resolve:
   * the metric was not fetched, answered with no rows, answered uncalculated, or (for a `component`
   * operand) did not carry that column. Each of those is the same "withheld, not zero" case the
   * union upstream exists to enforce, and a substituted number here would defeat it one layer down.
   *
   * Shared by both arithmetic kinds so they cannot disagree about what an unresolvable side is.
   */
  /**
   * The cohort a non-scalar metric's segments partition, summed from their own `n_customers`.
   *
   * One implementation for the two callers below -- the `segmentTotal` tile and a component-less
   * operand on a non-scalar metric are the SAME quantity, and a second copy is how a tile and the
   * rate beside it come to disagree about the population they are read over. A segment that stated
   * no count leaves the total UNKNOWN rather than smaller, so one absent count yields `undefined`
   * and never a sum over the rest. PLA-8815.
   */
  const segmentCountTotal = (identifier: string): number | undefined => {
    const counts = (distributionsByIdentifier[identifier]?.segments ?? []).map(
      segment => segment.count
    );

    return counts.length === 0 || counts.some(count => count === undefined)
      ? undefined
      : (counts as number[]).reduce((total, count) => total + count, 0);
  };

  /**
   * The same operand, resolved against a NON-SCALAR response.
   *
   * A fallback and never a first choice: a metric that answers scalar keeps the read below
   * unchanged, and this runs only where the scalar parse found no calculated row -- which for a
   * non-scalar response is every row, since it carries no `metric_value` column at all. Without it
   * an arithmetic tile over O-COM-032 resolves both sides `undefined` and draws empty over a
   * response that answered in full. A component-less operand is the segment total, because a metric
   * with no `metric_value` has no other whole to be a part of. PLA-8815.
   */
  const distributionOperandValue = (operand: RatioOperand): number | undefined => {
    const parsed = distributionsByIdentifier[operand.from];

    if (!parsed) {
      return undefined;
    }

    if (operand.component === undefined) {
      return segmentCountTotal(operand.from);
    }

    const component = parsed.components[operand.component];

    return typeof component === 'number' ? component : undefined;
  };

  const operandValue = (operand: RatioOperand): number | undefined => {
    const current = (resultsByIdentifier[operand.from] ?? [])[0];

    if (!current || current.status !== 'calculated') {
      return distributionOperandValue(operand);
    }

    if (operand.component === undefined) {
      return current.value;
    }

    const component = current.components[operand.component];

    return typeof component === 'number' ? component : undefined;
  };

  /**
   * The outcome for one identifier, once the card-level state it can carry has been recorded.
   *
   * `undefined` means "no reading to take from this", which covers two cases that must NOT be
   * conflated further downstream: never fetched (no side effect at all — not asking is not failing)
   * and fetched-and-failed (recorded in `failedStates`, so the card reports the backend's failure
   * instead of a blank). Shared by the scalar and the distribution branches so the two cannot
   * disagree about what a failed fetch does to the card around them.
   */
  const readingOutcome = (identifier: string): MetricFetchOutcome | undefined => {
    const outcome = outcomes[identifier];

    if (!outcome) {
      return undefined;
    }

    const state = cardStateFromOutcome(outcome);

    if (state) {
      failedStates[identifier] = state;

      return undefined;
    }

    servedOutcomes.push(outcome);

    return outcome;
  };

  // Keyed by TILE key, not by identifier: two distribution tiles of this card read the same
  // O-COM-032 response on different dimensions of it, and the body asks for them by tile key.
  const distributions: Record<string, CardDistribution> = {};

  entries
    .filter(entry => isSegmentTile(entry.source))
    .forEach(entry => {
      const { identifier } = entry.source as { identifier: string };

      identifiers.push(identifier);

      if (!readingOutcome(identifier)) {
        return;
      }

      const parsed = distributionsByIdentifier[identifier];

      // Answered, but with nothing calculated — or answered scalar, which for a tile declared
      // `distribution` is a contract change, not a reading. Either way the block draws nothing and
      // contributes NO `insufficientData` reason: the only reason code a non-scalar response carries
      // is `governed_non_scalar_grain` on its own marker row, which states the metric's SHAPE and
      // says nothing about whether it could be calculated. Reporting that as the card's reason is
      // precisely the defect this path replaces. PLA-8815.
      if (!parsed) {
        return;
      }

      distributions[entry.key] = {
        key: entry.key,
        label: entry.label,
        dimension: parsed.dimension,
        segments: parsed.segments
      };
    });

  // Hoisted rather than filtered inline: `entries` is one longer than `metrics` on any card that
  // also has a segment tile, and the evidence pass below reads both.
  const metricEntries = entries.filter(entry => !isSegmentTile(entry.source));

  const metrics: CardMetric[] = metricEntries.map(entry => {
    const base = { key: entry.key, label: entry.label, format: entry.format };

    // NOT pushed to `unservable` and NOT contributing to `failedStates`: `unservable` means the
    // contract cannot fill this tile AT ALL, while a ratio tile CAN be filled and merely was not
    // this request. An unresolved ratio behaves exactly like the never-fetched `metric` case below —
    // a silent `value: null`, no side effect on the card's own state.
    if (entry.source.kind === 'ratio') {
      const { numerator, denominator } = entry.source;

      identifiers.push(numerator.from, denominator.from);

      const top = operandValue(numerator);
      const bottom = operandValue(denominator);

      // A zero denominator is not a number either. `Infinity`/`NaN` would render as a figure.
      if (top === undefined || bottom === undefined || bottom === 0) {
        return { ...base, value: null };
      }

      // NOT clamped to 100. The two operands are independent readings, so a quotient above 1 is a
      // real property of the tenant's data (UAT `generic` reads 112.38% eligible-active) and
      // capping it would report a number nothing measured. PLA-8815.
      return { ...base, value: asPercentage(top, bottom) };
    }

    // Same null-safety as `ratio` above, and the same non-participation in the card's state: a
    // remainder that could not be worked out is an empty tile, not a card-level failure.
    if (entry.source.kind === 'difference') {
      const { minuend, subtrahend } = entry.source;

      identifiers.push(minuend.from, subtrahend.from);

      const left = operandValue(minuend);
      const right = operandValue(subtrahend);

      if (left === undefined || right === undefined) {
        return { ...base, value: null };
      }

      // Not floored at zero: a subtrahend larger than its minuend is a real disagreement between
      // two readings, and hiding it behind a 0 makes the two rings silently stop adding up.
      return { ...base, value: left - right };
    }

    /* The cohort a non-scalar metric's segments partition. Its own kind rather than a `component`
       because no column on the response states it -- see `segmentCountTotal` for why a partial
       answer empties the tile instead of under-reporting the population it heads. PLA-8815. */
    if (entry.source.kind === 'segmentTotal') {
      const { identifier } = entry.source;

      identifiers.push(identifier);

      if (!readingOutcome(identifier)) {
        return { ...base, value: null };
      }

      const total = segmentCountTotal(identifier);

      return { ...base, value: total === undefined ? null : total };
    }

    // A `component` tile on a non-scalar response: one population-level column, read as a figure.
    // No delta, for the same reason the scalar `component` path carries none — the movement would
    // be `metric_value`'s, which this response does not even have.
    if (entry.source.kind === 'distribution') {
      const { identifier, component } = entry.source;

      identifiers.push(identifier);

      if (!readingOutcome(identifier)) {
        return { ...base, value: null };
      }

      const reading = component
        ? (distributionsByIdentifier[identifier] ?? { components: {} }).components[component]
        : undefined;

      return typeof reading === 'number' ? { ...base, value: reading } : { ...base, value: null };
    }

    if (entry.source.kind !== 'metric') {
      // Still fetched when it is `derived`: the metric is real and the card needs it, even though
      // this particular tile reads a component of it that no column carries. Dropping the fetch
      // would empty the cards that are served ENTIRELY by derived tiles.
      if (entry.source.kind === 'derived') {
        identifiers.push(entry.source.from);
        unservable.push({
          metricKey: entry.key,
          reason: 'derived',
          from: entry.source.from,
          label: entry.label
        });
      } else {
        unservable.push({ metricKey: entry.key, reason: 'unmapped', label: entry.label });
      }

      return { ...base, value: null, notConfigured: true };
    }

    identifiers.push(entry.source.identifier);

    // Never fetched at all — a caller that did not ask for this metric — or fetched and failed.
    // Neither is a reading: the tile stays empty and the card does not claim `ok` on its strength.
    const outcome = readingOutcome(entry.source.identifier);

    if (!outcome) {
      // A failure is carried onto the tile as well as into the card's own verdict: a card is `ok`
      // as soon as ONE sibling reads, and without this the tile that failed is indistinguishable
      // from a tile the backend simply had no number for. Absent when the metric was never fetched
      // at all, which is not a failure of anything. PLA-8828.
      const failed = failedStates[entry.source.identifier];

      return { ...base, value: null, ...(failed ? { dataState: failed } : {}) };
    }

    // The Product Readiness schema answers with no `metric_value` and no `calculation_status`, so
    // its tile is built from its own columns rather than from the standard row. Read the other way
    // it would arrive as a reading of `null` carrying a manufactured empty status, and the card
    // would report that status as its reason for having no data. PLA-8825.
    const readinessRows = outcome.kind === 'rows' ? outcome.readiness : undefined;

    // Same treatment for the same reason, one schema family further on: `C-REV-002/003/004` carry
    // neither column either, and `fetchMetric` has been parsing them into `outcome.reversal` since
    // PLA-8825 with nothing reading it. PLA-8828.
    const reversal = outcome.kind === 'rows' ? outcome.reversal : undefined;

    const results = resultsByIdentifier[entry.source.identifier] ?? [];

    const metric = readinessRows
      ? // The scope's product, because one `-PR-` question answers with every product's cells and
        // the tile has to be told which are its own. PLA-8815.
        // A Customer 360 product tile has no scope; its own key names the product, and without it
        // every tile would read the first of the eleven product rows. PLA-8828.
        readinessCardMetric(base, readinessRows, scope?.product ?? productIdOfMetricKey(entry.key))
      : reversal
      ? reversalCardMetric(base, reversal)
      : toCardMetric(
          entry,
          results,
          entry.source.component,
          reasonsByIdentifier[entry.source.identifier]
        );

    // The rows a metric answered with BESIDES the one its tile reads -- 22 of them across six
    // Customer 360 metrics, dropped on the floor until now. The tile's own reading is unaffected:
    // `toCardMetric` resolves it from the same array and this takes nothing out of it.
    const panel = reversal
      ? reversalEvidencePanel(entry.label, reversal, entry.format)
      : detailEvidencePanel(entry.label, results, entry.format);

    if (panel) {
      evidencePanels.push({ identifier: entry.source.identifier, metricKey: entry.key, panel });
    }

    const factors = factorsOf(entry, results);

    if (factors) {
      Object.assign(metric, { factors });
    }

    if (metric.value === null && metric.unavailableStatus) {
      // The reason code is the specific governed answer; the calculation_status is the fallback
      // when a metric is fetched-but-uncalculated with no code attached.
      insufficientReasons.push(metric.unavailableReasonCode ?? metric.unavailableStatus);
    }

    return metric;
  });

  // `ok` when ANY tile has a reading. A card with one calculated metric and three withheld ones is
  // still worth drawing — the withheld tiles carry their own emptiness, which is the per-metric half
  // of the state model (plan § II.7).
  //
  // A resolved distribution counts as a reading on exactly the same terms, and this is what fixes
  // the reported bug: Customer Lifecycle & Product Holdings drew `insufficientData` over six
  // calculated lifecycle-stage rows, because a segment set is not a `CardMetric` and nothing else
  // asked whether one had arrived. `distributions` is only ever populated from calculated rows, so
  // it carries the same withheld-not-zero guarantee `metric.value !== null` does. PLA-8815.
  //
  // A `valueText` counts on the same terms and for the same reason. A categorical or status-code
  // reading answers in a WORD and carries `value: null` by design, so counting figures alone
  // reported Contact Restrictions -- whose only metric is `C-COM-021`, `calculated` /
  // `not_blocked` -- as a card that could not be calculated, over a reading that says the customer
  // may be contacted. `toCardMetric` only ever sets `valueText` off a `calculated` row. PLA-8828.
  // A readiness row's governed state is a reading too: `suppressed` with no score is an answer.
  const hasReading =
    metrics.some(
      metric =>
        metric.value !== null ||
        metric.valueText !== undefined ||
        Boolean(metric.readiness?.some(row => row.readiness_state != null))
    ) || Object.keys(distributions).length > 0;

  // Read per IDENTIFIER rather than per tile, for both fields below: a card's identifiers are the
  // fetch unit, and a tile is not. Going through the tiles would drop the confidence and the basis a
  // `ratio`/`difference` operand carried (those tiles hold neither), and would double-count nothing
  // in return -- the mean below de-duplicates by identifier anyway.
  const cardIdentifiers = identifiersForCard(cardKey, scope, entries);

  const whyThisResult = cardIdentifiers
    .map(identifier => calculationBasisOf(resultsByIdentifier[identifier] ?? []))
    .find(Boolean);

  /**
   * Every metric's reasons merged into ONE ranked list, which is what the box draws.
   *
   * Unlike `whyThisResult` above, which takes the FIRST basis and stops: a basis explains how one
   * reading was computed and three of them concatenated would be three answers to one question,
   * while reasons are a selection the backend already de-duplicated by `wtr_aggregation_group` and
   * ranked across the whole response. `wtrReasonsOf` only orders within the one identifier it was
   * handed, so this merge re-ranks by the same `selectionRank` rule with registry order breaking
   * ties: a metric's rank-3 reason never sorts above another metric's rank-1.
   *
   * Carried UNRENDERED. `renderedCardReasons` in `data/wtr.ts` is what turns these into the
   * sentences the whybox draws, at the render site. PLA-8815, PLA-8828.
   */
  const rankOf = (value: number | null): number => (typeof value === 'number' ? value : Infinity);

  const whyThisResultReasons: WtrReason[] = cardIdentifiers
    .reduce(
      (kept, identifier, order) => [
        ...kept,
        ...(reasonsByIdentifier[identifier] ?? []).map(reason => ({ reason, order }))
      ],
      [] as { reason: WtrReason; order: number }[]
    )
    .sort(
      (left, right) =>
        rankOf(left.reason.selectionRank) - rankOf(right.reason.selectionRank) ||
        left.order - right.order
    )
    .map(entry => entry.reason);

  // PLA-8815 / PLA-8828 INTERIM — remove this block together with `data/cardConfidence.ts` once a
  // card payload carries its own score and band, which `Calculation & Governance Rules` G32 says it
  // must. The opt-in names the LAYER, so the identifiers assembled here are scored against that
  // layer's eligibility table and against no other.
  const confidence = interimCardConfidence
    ? INTERIM_CARD_CONFIDENCE[interimCardConfidence](
        cardIdentifiers.map(identifier => ({
          identifiers: [identifier],
          confidence: confidenceOf(resultsByIdentifier[identifier] ?? [])
        }))
      )
    : undefined;

  const state: CardDataState = hasReading
    ? { kind: 'ok' }
    : worstOf(Object.values(failedStates)) ??
      (insufficientReasons.length > 0
        ? // At least one tile WAS fetched and answered — the backend gave a real reason it could
          // not calculate, which is a more honest and more useful thing to tell the reader than the
          // generic "not configured" a card that was never fetchable at all gets below.
          { kind: 'insufficientData', reason: insufficientReasons[0] }
        : {
            // Nothing failed and nothing read: every tile on this card is one the contract cannot
            // fill. `missing` exists for exactly this — naming the absent source rather than drawing
            // a blank. The tile's LABEL, never its catalogue id or internal key — user-facing text.
            kind: 'notConfigured',
            missing: unservable.map(tile => tile.label).join(', ') || undefined
          });

  // DEMO ONLY, PLA-8828 2026-09-21 — `withDemoFallback` puts invented figures on the tiles this card
  // would render empty, and returns the fixture untouched for every tile that read. ONE call for both
  // layers, because the layer opt-in above already names which catalogue this card belongs to.
  return {
    // ...and `withDemoNarrative` puts the `Why this result?` paragraph and the `Evidence Inputs:` note
    // on the cards the wire gave neither. OUTSIDE the call above, because the rescue branch there
    // strips the paragraph. DEMO ONLY, PLA-8828 2026-09-22.
    fixture: withDemoNarrative(
      withDemoFallback(
        {
          cardKey,
          state,
          updatedAt: latestPeriodEnd(servedOutcomes),
          metrics,
          distributions,
          // Spread, so a card whose metrics answered with a single row each produces the identical
          // fixture it always did rather than one carrying an empty panel.
          ...(evidencePanels[0]
            ? { evidence: evidencePanels[0].panel, evidencePanels: [...evidencePanels] }
            : {}),
          // Spread for the same reason as `evidence` above: a card the catalogue does not score keeps
          // the fixture it always had, and `InsightCard` draws no pill at all on an absent field.
          ...(confidence ? { confidence } : {}),
          // `calculation_basis`, promoted off the first tile that carried one. The column is per-row and
          // `whyThisResult` is per-card, so the card takes the first answer in registry order rather
          // than concatenating several explanations of different readings into one paragraph. PLA-8828.
          ...(whyThisResult ? { whyThisResult } : {}),
          // Spread for the same reason: a card whose metrics named no reason keeps the fixture it always
          // had, and the box draws the `calculation_basis` paragraph alone. PLA-8815, PLA-8828.
          ...(whyThisResultReasons.length > 0 ? { whyThisResultReasons } : {})
        },
        tenantName,
        interimCardConfidence,
        entries
      ),
      tenantName,
      interimCardConfidence
    ),
    unservable,
    identifiers: Array.from(new Set(identifiers))
  };
};

export default assembleCard;
